import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, Phone, MapPin, Home, CheckCircle, ArrowRight, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

type Step = 1 | 2 | 3 | 4;

const RegistroPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [formData, setFormData] = useState({
    nome: '',
    sobrenome: '',
    email: '',
    senha: '',
    confirmarSenha: '',
    telefone: '',
    rua: '',
    bairro: '',
    numero_casa: '',
    cep: ''
  });

  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);

  const { registro } = useAuth();
  const navigate = useNavigate();
  const { empresaId } = useParams<{ empresaId?: string }>();

  // Função para aplicar máscara de telefone
  const formatarTelefone = (valor: string) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 2) {
      return numeros;
    } else if (numeros.length <= 7) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    } else {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
    }
  };

  // Função para aplicar máscara de CEP
  const formatarCEP = (valor: string) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 5) {
      return numeros;
    } else {
      return `${numeros.slice(0, 5)}-${numeros.slice(5, 8)}`;
    }
  };

  const buscarCEP = async (cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setIsCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await res.json();
      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }
      setFormData(prev => ({
        ...prev,
        rua: data.logradouro || '',
        bairro: data.bairro || ''
      }));
      toast.success('Endereço preenchido automaticamente!');
    } catch {
      toast.error('Erro ao buscar CEP');
    } finally {
      setIsCepLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let valorFormatado = value;

    if (name === 'telefone') {
      valorFormatado = formatarTelefone(value);
    } else if (name === 'cep') {
      valorFormatado = formatarCEP(value);
      const cepLimpo = value.replace(/\D/g, '');
      if (cepLimpo.length === 8) {
        buscarCEP(cepLimpo);
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: valorFormatado
    }));
  };

  // Validação por etapa
  const validarEtapa = (step: Step): boolean => {
    switch (step) {
      case 1:
        if (!formData.nome || !formData.sobrenome || !formData.email) {
          toast.error('Preencha todos os campos');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error('Email inválido');
          return false;
        }
        return true;
      case 2:
        if (!formData.senha || !formData.confirmarSenha) {
          toast.error('Preencha todos os campos');
          return false;
        }
        if (formData.senha.length < 6) {
          toast.error('A senha deve ter pelo menos 6 caracteres');
          return false;
        }
        if (formData.senha !== formData.confirmarSenha) {
          toast.error('As senhas não coincidem');
          return false;
        }
        return true;
      case 3:
        if (!formData.telefone) {
          toast.error('Preencha o telefone');
          return false;
        }
        const telefoneLimpo = formData.telefone.replace(/\D/g, '');
        if (telefoneLimpo.length < 10) {
          toast.error('Telefone inválido');
          return false;
        }
        return true;
      case 4:
        if (!formData.rua || !formData.bairro || !formData.numero_casa || !formData.cep) {
          toast.error('Preencha todos os campos');
          return false;
        }
        const cepLimpo = formData.cep.replace(/\D/g, '');
        if (cepLimpo.length < 8) {
          toast.error('CEP inválido');
          return false;
        }
        return true;
      default:
        return false;
    }
  };

  const proximaEtapa = () => {
    if (validarEtapa(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep((prev) => (prev + 1) as Step);
      } else {
        handleSubmit();
      }
    }
  };

  const etapaAnterior = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as Step);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const dadosRegistro = {
        nome: formData.nome,
        sobrenome: formData.sobrenome,
        email: formData.email,
        senha: formData.senha,
        telefone: formData.telefone,
        rua: formData.rua,
        bairro: formData.bairro,
        numero_casa: formData.numero_casa,
        cep: formData.cep
      };

      await registro(dadosRegistro);
      toast.success('Conta criada com sucesso!');
      navigate(empresaId ? `/menu/${empresaId}` : '/');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao criar conta');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    { id: 1, title: 'Dados Pessoais', icon: User },
    { id: 2, title: 'Segurança', icon: Lock },
    { id: 3, title: 'Contato', icon: Phone },
    { id: 4, title: 'Endereço', icon: MapPin }
  ];

  const progresso = ((currentStep - 1) / 3) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-3 sm:p-4 lg:p-6">
      <div className="w-full max-w-2xl">
        {/* Botão Voltar */}
        <Link
          to={empresaId ? `/menu/${empresaId}` : "/"}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4 sm:mb-6"
        >
          <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-sm sm:text-base">Voltar ao Menu</span>
        </Link>

        {/* Card de Registro */}
        <div className="bg-gradient-to-br from-gray-800/95 to-gray-900/95 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-gray-700/50 shadow-2xl backdrop-blur-sm">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
              <User className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-1 sm:mb-2">
              Criar Conta
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">
              Vamos começar! Preencha seus dados
            </p>
          </div>

          {/* Indicador de Progresso */}
          <div className="mb-6 sm:mb-8">
            {/* Barra de Progresso */}
            <div className="relative h-2 bg-gray-700/50 rounded-full overflow-hidden mb-6">
              <div
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 ease-out rounded-full"
                style={{ width: `${progresso}%` }}
              />
            </div>

            {/* Steps */}
            <div className="relative flex justify-between items-start">
              {/* Linha de conexão (desktop) */}
              <div className="hidden sm:block absolute top-6 left-[12%] right-[12%] h-0.5 bg-gray-700/50">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500 ease-out"
                  style={{ width: `${progresso}%` }}
                />
              </div>

              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isCompleted = currentStep > step.id;
                const isCurrent = currentStep === step.id;

                return (
                  <div key={step.id} className="flex-1 flex flex-col items-center relative z-10">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white scale-110 shadow-lg shadow-green-500/30'
                      : isCurrent
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white scale-110 shadow-lg shadow-blue-500/50'
                        : 'bg-gray-700/50 text-gray-400'
                      }`}>
                      {isCompleted ? (
                        <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6" />
                      ) : (
                        <StepIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                      )}
                    </div>
                    <span className={`text-[10px] sm:text-xs mt-2 text-center max-w-[60px] sm:max-w-none ${isCurrent ? 'text-white font-semibold' : 'text-gray-400'
                      }`}>
                      {step.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Conteúdo das Etapas */}
          <div className="min-h-[300px] sm:min-h-[350px]">
            {/* Etapa 1: Dados Pessoais */}
            {currentStep === 1 && (
              <div className="animate-fade-in space-y-4 sm:space-y-5">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Informações Pessoais</h2>
                  <p className="text-gray-400 text-sm">Como você gostaria de ser chamado?</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                      Nome
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                        placeholder="Seu nome"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                      Sobrenome
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      <input
                        type="text"
                        name="sobrenome"
                        value={formData.sobrenome}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                        placeholder="Seu sobrenome"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Etapa 2: Segurança */}
            {currentStep === 2 && (
              <div className="animate-fade-in space-y-4 sm:space-y-5">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Segurança</h2>
                  <p className="text-gray-400 text-sm">Crie uma senha segura</p>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    Senha
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    <input
                      type={mostrarSenha ? 'text' : 'password'}
                      name="senha"
                      value={formData.senha}
                      onChange={handleChange}
                      className="w-full pl-10 pr-12 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      placeholder="Mínimo 6 caracteres"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarSenha(!mostrarSenha)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {mostrarSenha ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                    </button>
                  </div>
                  {formData.senha && formData.senha.length < 6 && (
                    <p className="text-xs text-yellow-400 mt-1">A senha deve ter pelo menos 6 caracteres</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    Confirmar Senha
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    <input
                      type={mostrarConfirmarSenha ? 'text' : 'password'}
                      name="confirmarSenha"
                      value={formData.confirmarSenha}
                      onChange={handleChange}
                      className="w-full pl-10 pr-12 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      placeholder="Confirme sua senha"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarConfirmarSenha(!mostrarConfirmarSenha)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {mostrarConfirmarSenha ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                    </button>
                  </div>
                  {formData.confirmarSenha && formData.senha !== formData.confirmarSenha && (
                    <p className="text-xs text-red-400 mt-1">As senhas não coincidem</p>
                  )}
                  {formData.confirmarSenha && formData.senha === formData.confirmarSenha && formData.senha.length >= 6 && (
                    <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Senhas coincidem
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Etapa 3: Contato */}
            {currentStep === 3 && (
              <div className="animate-fade-in">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Contato</h2>
                  <p className="text-gray-400 text-sm">Como podemos entrar em contato?</p>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    Telefone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    <input
                      type="tel"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      placeholder="(11) 99999-9999"
                      autoFocus
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Etapa 4: Endereço */}
            {currentStep === 4 && (
              <div className="animate-fade-in space-y-4 sm:space-y-5">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Endereço</h2>
                  <p className="text-gray-400 text-sm">Digite o CEP e preencheremos o resto!</p>
                </div>

                {/* CEP primeiro — auto-preenche rua e bairro */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    CEP
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    <input
                      type="text"
                      name="cep"
                      value={formData.cep}
                      onChange={handleChange}
                      className="w-full pl-10 pr-10 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      placeholder="00000-000"
                      maxLength={9}
                      autoFocus
                    />
                    {isCepLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Rua e bairro serão preenchidos automaticamente</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                      Rua
                    </label>
                    <div className="relative">
                      <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      <input
                        type="text"
                        name="rua"
                        value={formData.rua}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                        placeholder="Nome da rua"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                      Número
                    </label>
                    <input
                      type="text"
                      name="numero_casa"
                      value={formData.numero_casa}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                      placeholder="123"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    Bairro
                  </label>
                  <input
                    type="text"
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 sm:py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                    placeholder="Nome do bairro"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Botões de Navegação */}
          <div className="flex gap-3 mt-6 sm:mt-8">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={etapaAnterior}
                className="flex-1 sm:flex-initial sm:px-6 bg-gray-700/50 hover:bg-gray-700 text-white py-2.5 sm:py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Voltar</span>
              </button>
            )}

            <button
              type="button"
              onClick={proximaEtapa}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2.5 sm:py-3 rounded-xl font-bold hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {currentStep === 4 ? (
                <>
                  {isLoading ? 'Criando conta...' : 'Finalizar'}
                  {!isLoading && <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />}
                </>
              ) : (
                <>
                  Próximo
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </>
              )}
            </button>
          </div>

          {/* Link para Login */}
          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-gray-400 text-xs sm:text-sm mb-3">
              Já tem uma conta?
            </p>
            <Link
              to={empresaId ? `/login/${empresaId}` : "/login"}
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm sm:text-base font-medium"
            >
              Fazer Login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4 sm:mt-6 space-y-4">
          <p className="text-gray-500 text-xs sm:text-sm">
            © 2025 DK SYSTEMS. Todos os direitos reservados.
          </p>
          <div className="flex justify-center">
            <Link 
              to={empresaId ? `/politica-privacidade/${empresaId}` : "/politica-privacidade"}
              className="text-gray-500 hover:text-blue-400 text-xs transition-colors underline decoration-gray-700 underline-offset-4"
            >
              Política de Privacidade
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistroPage;
